import { Button, ButtonProps, CopyIcon, IconButton } from '@farol-ds/react'
import classNames from 'classnames'
import { RefObject } from 'react'

import styles from './copy-content-button.module.scss'
import {
  applySelectionStyle,
  removeSelectionStyle,
  selectElementText,
  unselectText,
} from './functions'

export type CopyContentButtonProps = ButtonProps & {
  contentRef: RefObject<HTMLDivElement>
  shouldAllowCopy?: () => boolean
  enableIconButton?: boolean
  removePatterns?: RegExp[]
}

export function CopyContentButton(props: CopyContentButtonProps) {
  const {
    contentRef,
    onClick,
    shouldAllowCopy = () => true,
    enableIconButton = true,
    children,
    className,
    removePatterns,
    disabled = false,
    ...rest
  } = props

  const classes = classNames(styles.btn, className)

  const getSelectionText = (selection: Selection) => {
    let selectionText = selection.toString()
    removePatterns?.forEach((regex) => {
      selectionText = selectionText.replace(regex, '')
    })
    return selectionText
  }

  const handleButtonClick = (evt: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(evt)

    if (!contentRef.current || !shouldAllowCopy()) {
      return
    }

    const styleElement = applySelectionStyle()
    const selection = selectElementText(contentRef.current)
    if (!selection) return

    navigator.clipboard
      .writeText(getSelectionText(selection))
      .then(() => {
        // This is necessary so CopyContentTriggerUtil can call the feature usage mutation.
        // And it doesn't work unless there's selected text.
        window.dispatchEvent(new ClipboardEvent('copy'))
      })
      .catch((err) => {
        console.error(err)
      })
      .finally(() => {
        unselectText()
        styleElement && removeSelectionStyle(styleElement)
      })
  }

  return (
    <>
      {enableIconButton && (
        <IconButton
          className={styles.iconBtn}
          label="Copiar"
          kind="tertiary"
          size="md"
          icon={<CopyIcon />}
          onClick={handleButtonClick}
          disabled={disabled}
        />
      )}
      <Button
        {...rest}
        className={enableIconButton ? classes : className}
        leftIcon={<CopyIcon />}
        onClick={handleButtonClick}
        disabled={disabled}
      >
        {children}
      </Button>
    </>
  )
}
